import React, { useCallback, useEffect, useRef, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import SupportTicketModal from './SupportTicketModal';
import { getPlatformSupportTickets } from '../../services/api';
import { formatDateTime } from '../../utils/formatters';

const TICKET_STATUS_LABELS = { open: 'Открыт', in_progress: 'В работе', closed: 'Закрыт' };
const CATEGORY_LABELS = { technical: 'Техническая проблема', billing: 'Оплата и тариф', question: 'Вопрос по функциям', other: 'Другое' };
const PRIORITY_LABELS = { low: 'Низкий', normal: 'Обычный', high: 'Высокий', urgent: 'Срочно' };

const TicketsCard = ({ onCountChange }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sort, setSort] = useState('updated_desc');
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [openTicketId, setOpenTicketId] = useState(null);
  const searchDebounceRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await getPlatformSupportTickets({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        q: q || undefined,
        sort,
      });
      setTickets(data.tickets || []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, q, sort]);

  useEffect(() => { load(); }, [load]);

  const handleSearchInput = (value) => {
    setSearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setQ(value.trim()), 350);
  };

  // Счётчик на вкладке — тикеты, ждущие ответа платформы, а не просто «открытые»:
  // считаем только когда видим весь список без фильтров, иначе цифра врала бы.
  useEffect(() => {
    if (!statusFilter && !priorityFilter && !q && onCountChange) {
      onCountChange(tickets.filter((t) => t.status !== 'closed' && !t.last_message_from_platform).length);
    }
  }, [tickets, statusFilter, priorityFilter, q, onCountChange]);

  return (
    <section className="ui-card">
      <div className="ui-card-head platform-card-head-row">
        <h2>Тикеты поддержки</h2>
        <div className="support-toolbar">
          <input
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            placeholder="Поиск по теме или организации…"
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Все статусы</option>
            <option value="open">Открытые</option>
            <option value="in_progress">В работе</option>
            <option value="closed">Закрытые</option>
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="">Любой приоритет</option>
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="updated_desc">Сначала новые</option>
            <option value="oldest">Сначала старые</option>
            <option value="priority">По приоритету</option>
          </select>
        </div>
      </div>

      {loading && <LoadingSpinner />}
      {!loading && tickets.length === 0 && (
        <div className="ui-empty">Обращений пока нет</div>
      )}
      {!loading && tickets.length > 0 && (
        <div className="platform-org-table">
          {tickets.map((t) => {
            const awaitingUs = t.status !== 'closed' && !t.last_message_from_platform;
            return (
              <div className="platform-org-row" key={t.id} onClick={() => setOpenTicketId(t.id)} style={{ cursor: 'pointer' }}>
                <div className="platform-org-main">
                  <div className="platform-org-name">
                    {t.subject}
                    {awaitingUs && <span className="support-new-dot" title="Ждёт ответа поддержки" />}
                  </div>
                  <div className="ui-dim">
                    {t.org_name} · {CATEGORY_LABELS[t.category] || t.category} · обновлено {formatDateTime(t.updated_at)}
                  </div>
                </div>
                <span className={`support-pill priority-${t.priority}`}>{PRIORITY_LABELS[t.priority] || t.priority}</span>
                <span className={`ui-status ${t.status === 'closed' ? 'off' : 'on'}`}>
                  {TICKET_STATUS_LABELS[t.status] || t.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {openTicketId && (
        <SupportTicketModal
          ticketId={openTicketId}
          onClose={() => setOpenTicketId(null)}
          onChanged={load}
        />
      )}
    </section>
  );
};

export default TicketsCard;
